import { z } from "zod";
/**
 * §17.1 — the customer approval portal's contract, shared by the API and the SPA.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THREE DOORS, ONE ROOM. The owner asked for a link by email, a link by WhatsApp
 * and a QR printed on the sheet. All three carry the SAME signed token and all
 * three open the SAME page, which is why the portal is described once, here,
 * rather than three times in three screens.
 *
 * WHAT IS DELIBERATELY ABSENT FROM {@link ApprovalPortalView} IS THE POINT OF
 * THE FILE. This is the ONLY unauthenticated surface in a system holding the
 * GSTIN, the PAN, the bank details and every document the business has issued,
 * so the payload is an ALLOWLIST rather than a projection of a database row:
 *
 *   - no internal id of anything — not the quotation's, not the customer's, not
 *     a line's. The document is named by its NUMBER, which the customer already
 *     has on paper.
 *   - no other customer, no other document, no list endpoint to reach one.
 *   - no user: no name, no role, no id. Who typed the quotation is not the
 *     recipient's business, and a name here would leak the staff of the firm.
 *   - no token. The page already holds it; echoing it back puts it in a React
 *     Query cache, a redux devtools frame and any error report the page makes.
 *   - no GSTIN, PAN or bank details of the SELLER beyond the trade name and
 *     address that are already printed on the sheet the customer is holding.
 *
 * EVERY FIGURE IS INTEGER PAISE AND COMES FROM THE STORED COLUMNS. The portal
 * recomputes nothing (§6): the server is the only authority on totals, and a
 * public page that re-derived one would be a second tax engine on the internet.
 * ═════════════════════════════════════════════════════════════════════════════
 */
/* -------------------------------------------------------------------------- */
/* 1. How a quotation came to be marked sent                                   */
/* -------------------------------------------------------------------------- */
/**
 * The channel `sent_at` was stamped through.
 *
 * `RECORDED` IS NOT A CHANNEL, AND THAT IS WHY IT IS IN THE SAME LIST. The owner
 * rings the customer or hands the sheet across the counter; nothing left this
 * machine, but the quotation IS with the customer and the dashboard's "waiting
 * for reply" count must say so. Recording it and actually sending it both stamp
 * the same column, so the column alone cannot tell them apart — which is exactly
 * why the channel is stored beside it. "We emailed it on the 4th" and "he says
 * he told them on the 4th" are different answers to a customer who claims they
 * never received it, and a single boolean cannot give either.
 */
export const QUOTATION_SEND_CHANNELS = ["EMAIL", "WHATSAPP", "LINK", "RECORDED"];
export const QuotationSendChannelSchema = z.enum(QUOTATION_SEND_CHANNELS);
/** The two answers a customer may give. Nothing else is a decision. */
export const QUOTATION_DECISIONS = ["APPROVED", "REJECTED"];
export const QuotationDecisionSchema = z.enum(QUOTATION_DECISIONS);
/* -------------------------------------------------------------------------- */
/* 2. The app side — recording a send, and issuing a link                      */
/* -------------------------------------------------------------------------- */
/**
 * ═════════════════════════════════════════════════════════════════════════════
 * THE TWO CHANNELS A PERSON MAY CLAIM, as opposed to the two a machine observes.
 *
 * `EMAIL` IS NOT HERE, AND THAT IS THE WHOLE POINT OF SPLITTING THE LIST. Only
 * `POST /quotations/:id/email` may write `EMAIL`, because only it knows an SMTP
 * server accepted the message; only the portal may write `LINK`, because only it
 * knows the customer opened the page. A hand-typed `EMAIL` would put a claim the
 * system cannot support into the one record somebody reads back six months later
 * when a customer says they never received it.
 *
 * WHERE IT IS USED, SINCE 2026-08-20: `QuotationStatusUpdateSchema` in
 * documents.ts. There used to be a second route — `POST /:id/record-sent` — with
 * its own two buttons in the SPA beside a third that only moved the status, and
 * the owner asked for ONE control ("we already have mark as sent, so remove sent
 * on whatsapp and record as sent"). They were NOT the same act: marking the
 * status left `sent_at` and `sent_channel` NULL, so deleting the other two would
 * have thrown away both the ageing clock behind "waiting for reply" and the
 * record of HOW an offer went out. The surviving control carries the channel
 * instead, and the route it posts to now stamps the columns.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const CLAIMABLE_QUOTATION_SEND_CHANNELS = ["WHATSAPP", "RECORDED"];
export const ClaimableQuotationSendChannelSchema = z.enum(CLAIMABLE_QUOTATION_SEND_CHANNELS);
/**
 * `POST /public/quotations/decision`.
 *
 * THE TOKEN IS IN THE BODY, NOT THE PATH, for the reason {@link
 * ApprovalLinkIssued.url} gives: a token in a URL is a token in a log file.
 */
export const ApprovalDecisionSchema = z
    .object({
    token: z.string().min(1).max(512),
    decision: QuotationDecisionSchema,
    /**
     * WHO IS ANSWERING, AND IT IS REQUIRED. This is the whole provenance of a
     * bearer decision: nobody signed in, so the only thing distinguishing "the
     * purchasing manager approved it" from "somebody who found the sheet" is a
     * name typed at the moment of answering, stored beside the timestamp and the
     * IP. An anonymous approval is a decision nobody can be asked about later.
     */
    name: z.string().trim().min(1).max(191),
    /** Optional — "please revise the delivery date". Shown to the owner only. */
    note: z.string().trim().max(1000).optional(),
})
    .strict();
/** `POST /public/quotations/view` and `/pdf` — the token, and nothing else. */
export const ApprovalTokenSchema = z
    .object({ token: z.string().min(1).max(512) })
    .strict();
/**
 * THE ONE SENTENCE EVERY REFUSED TOKEN GETS, whatever is wrong with it.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * EXPIRED, TAMPERED, TRUNCATED, UNKNOWN, FOR A QUOTATION THAT WAS CANCELLED, OR
 * SIMPLY MADE UP — ALL ONE ANSWER, ONE STATUS, ONE STRING.
 *
 * Any difference between those cases is an oracle. "Expired" tells a prober that
 * the token was otherwise valid, so a guessed one that comes back "expired"
 * confirms a signature forgery worked; "no such quotation" confirms the opposite
 * about an id. The distinctions are useful to exactly one party and it is not
 * the customer, who in every case needs the same next action: ring the business
 * and ask for the quotation again.
 *
 * IT IS A KEY, NOT A SENTENCE, so the portal renders it in English or Marathi
 * (§11.4) — the reader is a customer, not the owner. And the status is 410 Gone
 * rather than 404, deliberately: 404 is what a route answers when the FLAG is
 * off (§18.2), and those two must stay tellable apart by the SPA so it can show
 * "this link no longer works" instead of "this app has no such page".
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const APPROVAL_LINK_REFUSED = "approval.linkRefused";
//# sourceMappingURL=approval.js.map